import { FC } from "react"
import { AnySchema, FormSchema, ParamGradeLabel } from "@/lib/form/schema"
import { EnumsOf } from "@/lib/utils"

/**
 * Props of a widget
 */
export interface WidgetBaseProps<Schema extends AnySchema = any> {
  /**
   * Path to the field
   *
   * @example
   * `address.street`
   * `phones.2.countryCode`
   */
  path: string

  /**
   * Schema of this parameter
   */
  schema: Schema

  /**
   * Schema of the form
   */
  formSchema?: FormSchema
}

/**
 * Widget is react component that renders a
 * parameter schema
 */
export type Widget<P extends WidgetBaseProps = WidgetBaseProps> = FC<P>

export const BreakdownType = {
  BREAK_DOWN: "BREAK_DOWN",
  POWER_FAILURE: "POWER_FAILURE",
  CHANGE_OVER: "CHANGE_OVER",
  OTHER: "OTHER",
} as const
export type BreakdownType = EnumsOf<typeof BreakdownType>

export const FormParamStatus = {
  NEW: "NEW",
  PENDING: "PENDING",
  FILLED_COMPLETE: "FILLED_COMPLETE",
  FILLED_PENDING_APPROVAL: "FILLED_PENDING_APPROVAL",
  REJECTED_PENDING_APPROVAL: "REJECTED_PENDING_APPROVAL",
  REJECTED_APPROVED: "REJECTED_APPROVED",
  SUBMITTED_PENDING_APPROVAL: "SUBMITTED_PENDING_APPROVAL",
  DRAFT: "DRAFT",
  REMARK: "REMARK",
  SUBMITTED: "SUBMITTED",
  COMPLETED: "COMPLETED",
  REJECTED: "REJECTED",
  RESAMPLED: "RESAMPLED",
  INVALID: "INVALID",
  VALID: "VALID",
  DISABLED: "DISABLED",
  HANDOVER_NEW: "HANDOVER_NEW",
  HANDOVER_DRAFT: "HANDOVER_DRAFT",
  HANDOVER_SUBMITTED: "HANDOVER_SUBMITTED",
  NOT_APPLICABLE: "NOT_APPLICABLE",
  ...BreakdownType,
} as const
export type FormParamStatus = EnumsOf<typeof FormParamStatus>

export const FormStatus = {
  PENDING: "PENDING",
  DRAFT: "DRAFT",
  SUBMITTED: "SUBMITTED",
} as const
export type FormStatus = (typeof FormParamStatus)[keyof typeof FormParamStatus]

export function isSubmittedLikeStatus(status?: FormParamStatus): boolean {
  return (
    status === FormParamStatus.SUBMITTED ||
    status === FormParamStatus.HANDOVER_SUBMITTED ||
    status === FormParamStatus.COMPLETED
  )
}

export interface FormData {
  handoverFromEmployeeCode?: string
  handoverFromEmployeeSessionId?: string
  products: ProductFormData[]
}

export interface ProductFormData {
  id: string
  status?: FormParamStatus
  handover?: {
    handoverFromEmployeeCode: string
    handoverToEmployeeCode: string
  }
  productionLines: ProductionLineFormData[]
}

export interface ProductionLineFormData {
  id: string
  status?: FormParamStatus
  machines: MachineFormData[]
}

export interface MachineFormData {
  id: string
  breakdown: BreakdownFormData[]
  internalProcesses: InternalProcessFormData[]
}

export interface BreakdownFormData {
  id: string
  type: BreakdownType
  remark: string

  /**
   * UTC ISO timestamp
   */
  startedAt: string

  /**
   * UTC ISO timestamp
   */
  endedAt: string
}

export interface InternalProcessFormData {
  id: string
  parameters: ParamData[]
}

export type ParamData =
  | SingleValueParamData
  | WindowParamFormData
  | FormulaParamData
export type ResampledData<T> = Omit<
  T,
  "resample" | "id" | "formula" | "window" | "resampleCleared"
>

export interface BaseParamData {
  id: string
  remark?: string
  gradeLabel?: ParamGradeLabel
  status?: FormParamStatus
  resampleCleared?: boolean

  /**
   * UTC ISO timestamp
   */
  recordedAt?: string
}

export interface SingleValueParamData<V = any> extends BaseParamData {
  value?: V
  resample?: ResampledData<SingleValueParamData>
}

export interface FormulaParamData<V = any> extends BaseParamData {
  formula: boolean
  resample?: ResampledData<FormulaParamData>
  value: V
}

export interface WindowParamFormData<V = any> {
  id: string
  window: WindowData<V>[]
}

export interface WindowData<V = any> extends BaseParamData {
  value?: V
  remark?: string
  sequenceOrder?: number
  overtime?: boolean
  early?: boolean
  resample?: ResampledData<WindowData>

  /**
   * UTC ISO timestamp
   */
  startTime: string

  /**
   * UTC ISO timestamp
   */
  endTime: string

  /**
   * UTC ISO timestamp
   */
  recordedAt?: string
}
